
import { Component, Vue, Watch, Prop } from 'vue-property-decorator'
import { AssignRepairDept, ListLeaks, MarkAsInternalRepair } from '@/api/leaks'
import { ILeakPoint, IDept, DeptType, IAllDeptInfo, LeakStatus, IUser, GetLeakStatus } from '@/api/types'
import LeakBriefInfoCard from '../LeakBriefInfoCard/index.vue'
import InfiniteScroll from '@/components/InfiniteScroll/index.vue'
import Pagination from '@/components/Pagination/index.vue'
import { formatDate } from '@/utils/index'
import { IsMobile } from '@/utils/check'

interface StatusOption {
  value: number
  label: string
}

@Component({
  name: 'LeakList',
  components: {
    LeakBriefInfoCard,
    InfiniteScroll,
    Pagination
  }
})
export default class extends Vue {
  @Prop({ default: () => ({
    showDetail: true
  })}) private actionConfig!: {
    showDetail: boolean
  }

  @Prop({ default: null }) private deptList!: IAllDeptInfo[]| []
  @Prop({ default: null }) private userList!: IUser[]| []
  @Prop({ default: false }) private shouldReload!: boolean
  @Prop({ default: () => ({}) }) private searchParams!: object
  @Prop({ default: false }) private batchOperationPermission!: boolean

  private list: ILeakPoint[] = []
  private total = 0
  private listLoading = false
  private listQuery = {
    status: [1,2],
    keywords: [] as string[],
    addressKeywords: [] as string[],
    createTimeStart: '',
    createTimeEnd: '',
    reportTimeStart: '',
    reportTimeEnd: '',
    page: 1,
    pageSize: 50
  }

  private saving = false

  // 批量操作相关属性
  private multipleSelection: ILeakPoint[] = []
  private showBatchOperations = false
  private batchOperationType = 'assignDept'
  private selectedDeptId = ''

  private assignDeptDialogVisible = false
  private assignDeptForm: { deptId: number | undefined } = { deptId: undefined }
  private assignDeptRules = {
    deptId: [{ required: true, message: '请选择维修队伍', trigger: 'change' }]
  }
  private currentAssignLeakId = -1

  // 记录上一次搜索参数用于比较
  private previousSearchParams: object = {}

  // 移动端搜索弹窗
  private showSearchDialog = false

  // 日期范围选择器的值
  private dateRange: [string, string] | null = null

  // 年度筛选选择器的值
  private selectedYear: number | null = new Date().getFullYear()

  // 年度选项（近10年）
  private get yearOptions(): number[] {
    const currentYear = new Date().getFullYear()
    const years: number[] = []
    for (let i = 0; i < 10; i++) {
      years.push(currentYear - i)
    }
    return years
  }

  get canPerformBatchOperation(): boolean {
    if (this.batchOperationType === 'assignDept') {
      return this.multipleSelection.length > 0 && !!this.selectedDeptId
    }
    return false
  }

  // 移动模式标记

  get isMobile() {
    return IsMobile()
  }

  // 检查是否有批量操作的权限
  get hasBatchOperationPermission() {
    return this.batchOperationPermission
  }

  get statusOptions(): StatusOption[] {
    return [
      { value: LeakStatus.Unreported, label: GetLeakStatus(LeakStatus.Unreported) },
      { value: LeakStatus.NeedAssignRepairDept, label: GetLeakStatus(LeakStatus.NeedAssignRepairDept) },
      { value: LeakStatus.NeedAssignRepairUser, label: GetLeakStatus(LeakStatus.NeedAssignRepairUser) },
      { value: LeakStatus.Repairing, label: GetLeakStatus(LeakStatus.Repairing) },
      { value: LeakStatus.Repaired, label: GetLeakStatus(LeakStatus.Repaired) },
      { value: LeakStatus.NoNeedRepair, label: GetLeakStatus(LeakStatus.NoNeedRepair) },
      { value: LeakStatus.CantRepair, label: GetLeakStatus(LeakStatus.CantRepair) }

    ]
  }

  private get repairDeptList(): IDept[] {
    return this.deptList.filter(dept => dept.dept.type === DeptType.Repair).map(dept => dept.dept)
  }

  // 全选相关计算属性
  get isAllSelected(): boolean {
    if (this.list.length === 0 || this.multipleSelection.length === 0) {
      return false
    }
    return this.multipleSelection.length === this.list.length
  }

  get isIndeterminate(): boolean {
    return this.multipleSelection.length > 0 && this.multipleSelection.length < this.list.length
  }

  // 监听变化，处理indeterminate状态
  @Watch('isIndeterminate')
  onIndeterminateChange() {
    this.updateSelectAllCheckboxState()
  }

  @Watch('isAllSelected')
  onAllSelectedChange() {
    this.updateSelectAllCheckboxState()
  }

  // 更新全选checkbox的状态
  private updateSelectAllCheckboxState() {
    this.$nextTick(() => {
      const checkbox = this.$refs.selectAllCheckbox as HTMLInputElement
      if (checkbox) {
        checkbox.indeterminate = this.isIndeterminate
      }
    })
  }

  created() {
    // 确保无权限用户不会显示批量操作弹窗
    if (!this.hasBatchOperationPermission) {
      this.showBatchOperations = false
    }

    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize)


    // 初始化参数
    if (this.searchParams && Object.keys(this.searchParams).length > 0) {
      this.listQuery = {
        ...this.listQuery,
        ...this.searchParams,
        page: 1
      }
      this.previousSearchParams = {...this.searchParams}
    }

    // 初始加载
    this.$nextTick(() => {
      this.getLeakList()
    })
  }

  // 移除单独的初始化方法，避免重复触发
  @Watch('searchParams', { deep: true })
  private updateSearchParams() {
    if (!this.searchParams || Object.keys(this.searchParams).length === 0) return

    // 只有参数真正变化时才重新加载数据
    const hasRealChanges = JSON.stringify(this.searchParams) !== JSON.stringify(this.previousSearchParams || {})

    if (hasRealChanges) {
      // 合并搜索参数到listQuery
      this.listQuery = {
        ...this.listQuery,
        ...this.searchParams,
        page: 1 // 重置到第一页
      }
      this.previousSearchParams = {...this.searchParams}

      // 避免正在加载时重复触发
      if (!this.listLoading) {
        this.resetInfiniteScroll()
      }
    }
  }

  mounted() {
    // 初始化全选checkbox的状态
    this.updateSelectAllCheckboxState()

    // 确保组件挂载后正确计算高度
    this.$nextTick(() => {
      const scrollContainer = this.$el.querySelector('.leak-infinite-scroll') as HTMLElement
      if (scrollContainer) {
        scrollContainer.style.height = `${scrollContainer.offsetHeight}px`
      }
    })
  }

  destroyed() {
    window.removeEventListener('resize', this.handleResize)
  }

  private formatDate(date: string) {
    return formatDate(date, 'YYYY-MM-DD HH:mm')
  }

  private handleResize() {
    // 检测窗口大小变化
    this.resetInfiniteScroll()
    this.$forceUpdate()
  }

  @Watch('shouldReload')
  private async onShouldReloadChange() {
    if (this.shouldReload && !this.listLoading) {
      this.resetInfiniteScroll()
      this.$emit('update:shouldReload', false)
      this.$emit('reload-done')
    }
  }

  private async getLeakList() {
    // 添加防抖，防止重复请求
    if (this.listLoading) {
      console.log('正在加载中，忽略请求')
      return
    }

    // 打印请求参数，方便调试
    console.log('请求参数:', { ...this.listQuery })

    this.listLoading = true

    try {
      const { data } = await ListLeaks({
        ...this.listQuery
      })

      // 根据 request.ts 中的响应处理，直接获取响应数据
      if (this.isMobile) {
        // 移动模式下，如果是第一页则重置列表，否则追加
        if (this.listQuery.page === 1) {
          this.list = data.items || []
        } else {
          this.list = [...this.list, ...(data.items || [])]
        }
      } else {
        // PC模式下，始终使用当前页的数据
        this.list = data.items || []
      }
      this.total = data.total || 0

      console.log('加载成功，数据条数:', this.list.length, '总条数:', this.total)
    } catch (error) {
      console.error('获取漏点列表失败', error)
      this.$message.error('获取漏点列表失败，请重试')
      this.list = []
      this.total = 0
    } finally {
      this.listLoading = false
    }
  }

  // 处理无限滚动的加载更多事件
  private handleLoadMore({ page, limit }: { page: number, limit: number }) {
    this.listQuery.page = page
    this.getLeakList()
  }

  private refreshList() {
    this.resetInfiniteScroll()
  }

  // 重置无限滚动组件或分页组件
  private resetInfiniteScroll() {
    if (this.listLoading) return // 正在加载时不重复触发

    this.listQuery.page = 1
    this.multipleSelection = [] // 重置选择

    if (this.isMobile) {
      // 移动端重置无限滚动组件
      this.list = []
      if (this.$refs.infiniteScroll) {
        (this.$refs.infiniteScroll as any).reset()
      }
    }

    this.getLeakList()
  }

  private handleDetail(row: ILeakPoint) {
    this.$emit('detail', row.id)
  }

  private handleCreateLeak() {
    this.$emit('create')
  }

  // 检查行是否被选中
  private isRowSelected(row: ILeakPoint): boolean {
    return this.multipleSelection.some(item => item.id === row.id)
  }

  // 全选/取消全选
  private handleSelectAllChange(val: boolean) {
    if (val) {
      // 全选
      this.multipleSelection = [...this.list]
    } else {
      // 取消全选
      this.multipleSelection = []
    }
  }

  // 处理卡片选择状态变化
  private handleCardSelection(id: string | number, selected: boolean) {
    if (this.isMobile) return // 移动端不处理选择

    console.log('处理选择状态变化:', id, selected)

    const row = this.list.find(item => item.id === Number(id))
    if (!row) return

    if (selected) {
      // 如果当前选择不包含该行，添加它
      if (!this.isRowSelected(row)) {
        this.multipleSelection.push(row)
      }
    } else {
      // 如果包含该行，移除它
      const index = this.multipleSelection.findIndex(item => item.id === Number(id))
      if (index !== -1) {
        this.multipleSelection.splice(index, 1)
      }
    }
  }

  // 处理批量操作
  private handleBatchOperation() {
    // 首先检查权限
    if (!this.hasBatchOperationPermission) {
      this.$message({
        message: '您没有批量操作的权限',
        type: 'error'
      })
      this.showBatchOperations = false
      return
    }

    if (this.batchOperationType === 'assignDept' && this.selectedDeptId) {
      // 筛选状态为"待分配"的漏点
      const validLeaks = this.multipleSelection.filter(item => item.leakStatus === LeakStatus.NeedAssignRepairDept)

      if (validLeaks.length === 0) {
        this.$message({
          message: '选中的漏点中没有处于"待分配"状态的漏点',
          type: 'warning'
        })
        return
      }

      // 获取所有符合条件的漏点ID
      const selectedIds = validLeaks.map(item => item.id)

      // 如果有不符合条件的漏点，给出提示
      if (validLeaks.length < this.multipleSelection.length) {
        this.$message({
          message: `已选择${this.multipleSelection.length}个漏点，其中${validLeaks.length}个处于"待分配"状态，只会操作这些漏点`,
          type: 'info'
        })
      }

      // 通过事件将选中的ID和部门ID传递给父组件
      this.$emit('batch-assign', {
        leakIds: selectedIds,
        deptId: this.selectedDeptId
      })

      // 关闭对话框并清空选择
      this.showBatchOperations = false
      this.selectedDeptId = ''
    }
  }

  // 处理卡片的各种操作
  private handleOperation({ id, type }: { id: number, type: string }) {
    // 通过事件触发父组件中的详情组件，并设置初始模式

    if (type === 'internal-repair') {
      this.handleInternalRepair(id)
    } else if (type === 'assign') {
      this.currentAssignLeakId = id
      this.assignDeptDialogVisible = true
    } else {
      this.$emit('detail', id, type)
    }
  }

  private async confirmAssignDept() {
    let valid = false
    try {
      valid = await new Promise<boolean>((resolve) => {
        (this.$refs.assignDeptForm as any).validate((isValid: boolean) => {
          resolve(isValid)
        })
      })
    } catch (error) {
      valid = false
    }

    if (!valid) {
      this.$message.warning('请选择维修队伍')
      return
    }

    this.saving = true
    try {
      await AssignRepairDept({
        id: this.currentAssignLeakId,
        deptId: this.assignDeptForm.deptId as number
      })

      this.$message.success('分配维修队伍成功')
      this.assignDeptDialogVisible = false
      this.$emit('assign') // 发送更新成功事件
    } catch (error) {
      console.error('分配失败', error)
      this.$message.error('分配维修队伍失败')
    } finally {
      this.saving = false
      this.currentAssignLeakId = -1
      this.resetInfiniteScroll()
    }
  }

  private async handleInternalRepair(id: number) {
    await MarkAsInternalRepair({ id: id })
    this.$message.success('标记内部维修成功')
    this.resetInfiniteScroll()
  }

  // 计算属性：当前页码
  get currentPage(): number {
    return this.listQuery.page
  }

  // 计算属性：每页数量
  get pageSize(): number {
    return this.listQuery.pageSize
  }

  // 处理每页数量变化
  private handleSizeChange(val: number) {
    if (this.listLoading || val === this.listQuery.pageSize) return

    this.listQuery.pageSize = val
    this.listQuery.page = 1 // 切换每页数量时重置到第一页
    this.getLeakList()
  }

  // 处理页码变化
  private handleCurrentChange(val: number) {
    if (this.listLoading || val === this.listQuery.page) return

    this.listQuery.page = val
    this.getLeakList()
  }

  // 重置所有筛选条件和分页
  private resetAll() {
    if (this.listLoading) return

    this.multipleSelection = []
    this.listQuery.page = 1

    // 清空筛选条件
    this.listQuery.keywords = []
    this.listQuery.addressKeywords = []

    this.getLeakList()
  }
}
